import z from "zod";

import type { CustomEmoji } from "./custom-emoji";

/** Ensure HTML content is a string, and drop empty `<p>` tags. */
const contentSchema = z
  .string()
  .catch("")
  .transform((value) => (value === "<p></p>" ? "" : value));

/** Validate to Mastodon's date format, or use the current date. */
const dateSchema = z.string().datetime({ offset: true }).catch(new Date().toUTCString());

/** Validates individual items in an array, dropping any that aren't valid. */
function filteredArray<T extends z.ZodTypeAny>(schema: T) {
  return z
    .any()
    .array()
    .catch([])
    .transform((arr) =>
      arr
        .map((item) => {
          const parsed = schema.safeParse(item);
          if (!parsed.success)
            console.error("error in parsing", item, parsed.error);
          return parsed.success ? parsed.data : undefined;
        })
        .filter((item): item is z.infer<T> => Boolean(item)),
    );
}

/** Validates the string as an emoji. */
const emojiSchema = z
  .string()
  .refine((v) => /\p{Extended_Pictographic}/u.test(v));

/** Map a list of CustomEmoji to their shortcodes. */
function makeCustomEmojiMap(customEmojis: CustomEmoji[]) {
  return customEmojis.reduce<Record<string, CustomEmoji>>((result, emoji) => {
    result[`:${emoji.shortcode}:`] = emoji;
    return result;
  }, {});
}

/** zod schema to force the value into an object, if it isn't already. */
function coerceObject<T extends z.ZodRawShape>(shape: T) {
  return z.object({}).passthrough().catch({}).pipe(z.object(shape));
}

/** Validates a URL, and returns the relative path. */
const relativeUrlSchema = z
  .string()
  .url()
  .transform((url) => {
    const parsedUrl = new URL(url);
    return parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
  })
  .catch("");

/** Validates a boolean value represented as a string. */
const stringToBooleanSchema = z.union([
  z.boolean(),
  z.string().transform((val) => val === "true"),
]);

const emptyArraySchema = z.array(z.never()).catch([]);

export {
  coerceObject,
  contentSchema,
  dateSchema,
  emojiSchema,
  filteredArray,
  makeCustomEmojiMap,
  relativeUrlSchema,
  stringToBooleanSchema,
  emptyArraySchema,
};
